import { STORAGE_KEY, STORAGE_VERSION } from './contract';

export type Contrast = 'none' | 'dark' | 'light' | 'invert';
/** `readable` is Atkinson Hyperlegible, `dyslexic` is OpenDyslexic (named by the font, ticket §9). */
export type Font = 'none' | 'readable' | 'dyslexic';
/** `gray` is full desaturation (what was the Grayscale switch). */
export type Saturation = 'none' | 'low' | 'high' | 'gray';
export type TextSize = 0 | 1 | 2 | 3;
/** `none` keeps the site's own alignment. No justify: it is the hardest to read. */
export type Align = 'none' | 'left' | 'center' | 'right';
/** Read aloud speed, as SpeechSynthesisUtterance.rate. */
export type SpeechRate = 0.75 | 1 | 1.25 | 1.5;
export const SPEECH_RATES: readonly SpeechRate[] = [0.75, 1, 1.25, 1.5];

/** Every visitor setting. Defaults change nothing on the page. */
export interface Prefs {
  textSize: TextSize;
  align: Align;
  spacing: boolean;
  font: Font;
  contrast: Contrast;
  saturation: Saturation;
  links: boolean;
  guide: boolean;
  cursor: boolean;
  /** `null` = the visitor has not chosen, so follow the device's reduce-motion setting. */
  motion: boolean | null;
  images: boolean;
  /** Read aloud is on: its player shows. It never starts reading by itself. */
  speech: boolean;
  speechRate: SpeechRate;
}

export type BooleanPref =
  'spacing' | 'links' | 'guide' | 'cursor' | 'images' | 'speech';

export const BOOLEAN_PREFS: readonly BooleanPref[] = [
  'spacing',
  'links',
  'guide',
  'cursor',
  'images',
  'speech',
];

export const DEFAULT_PREFS: Readonly<Prefs> = Object.freeze({
  textSize: 0,
  align: 'none',
  spacing: false,
  font: 'none',
  contrast: 'none',
  saturation: 'none',
  links: false,
  guide: false,
  cursor: false,
  motion: null,
  images: false,
  speech: false,
  speechRate: 1,
});

/**
 * Anything that does not validate falls back to its default, one field at a
 * time, so a corrupt or hand-edited value resets that one setting and never
 * throws.
 */
export function sanitize(raw: unknown): Prefs {
  const out: Prefs = { ...DEFAULT_PREFS };
  if (!raw || typeof raw !== 'object') return out;
  const r = raw as Record<string, unknown>;

  if (r.textSize === 1 || r.textSize === 2 || r.textSize === 3)
    out.textSize = r.textSize;
  if (r.align === 'left' || r.align === 'center' || r.align === 'right')
    out.align = r.align;
  if (
    r.contrast === 'dark' ||
    r.contrast === 'light' ||
    r.contrast === 'invert'
  )
    out.contrast = r.contrast;
  // `font: true` and `grayscale: true` are the switches these replaced.
  if (r.font === 'readable' || r.font === 'dyslexic') out.font = r.font;
  else if (r.font === true) out.font = 'readable';
  if (
    r.saturation === 'low' ||
    r.saturation === 'high' ||
    r.saturation === 'gray'
  )
    out.saturation = r.saturation;
  else if (r.grayscale === true) out.saturation = 'gray';
  if (typeof r.motion === 'boolean') out.motion = r.motion;
  if (SPEECH_RATES.indexOf(r.speechRate as SpeechRate) !== -1)
    out.speechRate = r.speechRate as SpeechRate;
  BOOLEAN_PREFS.forEach((key) => {
    if (r[key] === true) out[key] = true;
  });
  return out;
}

/** In-memory fallback for browsers that block storage (private mode, settings). */
let memory: Prefs | null = null;

export function loadPrefs(): Prefs {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return sanitize(JSON.parse(stored));
  } catch (e) {
    // Storage blocked or the value is not JSON. Fall through.
  }
  return memory ? { ...memory } : { ...DEFAULT_PREFS };
}

export function savePrefs(prefs: Prefs): void {
  memory = { ...prefs };
  try {
    if (isDefault(prefs)) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: STORAGE_VERSION, ...prefs }),
      );
    }
  } catch (e) {
    // Storage blocked: the in-memory copy still carries this page view.
  }
}

/** True when nothing is changed. An unchosen `motion` counts as default. */
export function isDefault(prefs: Prefs): boolean {
  return (
    prefs.textSize === 0 &&
    prefs.align === 'none' &&
    prefs.contrast === 'none' &&
    prefs.font === 'none' &&
    prefs.saturation === 'none' &&
    prefs.motion === null &&
    prefs.speechRate === 1 &&
    BOOLEAN_PREFS.every((key) => !prefs[key])
  );
}

/** Pause animations is on when the visitor chose it, or has not chosen and the device asks. */
export function effectiveMotion(
  prefs: Prefs,
  deviceReducesMotion: boolean,
): boolean {
  return prefs.motion === null ? deviceReducesMotion : prefs.motion;
}

/** Another tab changed the settings. Returns an unsubscribe function. */
export function onExternalChange(callback: (prefs: Prefs) => void): () => void {
  const listener = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) callback(loadPrefs());
  };
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}
